import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { IoClose } from 'react-icons/io5';
import { FontSizes, FontWeights } from 'constants/Clementine';
import Numeral from 'numeral';
import _orderBy from 'lodash/orderBy';

const AAQ_QuoteCompare_TableColumn = [
    {
        id: ['product_name', 'carrier_name'],
        default: true,
        label: 'Product',
    },
    {
        id: 'current_rate',
        label: 'Current Rate',
    },
    {
        id: 'accumulation',
        label: 'Accumulation',
        type: 'currency',
    },
    {
        id: 'base_rate',
        label: 'Guaranteed Rate',
        type: 'percentage',
    },
    {
        id: 'surrender_charge',
        label: 'Surrender Period',
    },
    {
        id: 'annual_free_currency',
        label: 'Free Withdrawal %',
    },
];

const ALQ_QuoteCompare_TableColumn = [
    {
        id: ['product_name', 'carrier_name'],
        default: true,
        label: 'Product',

    },
    {
        id: ['ixn_health_category', 'tobacco'],
        label: 'Health Category',
    },
    {
        id: 'face_amount',
        label: 'Face Amount',
        type: 'currency',
    },
    {
        id: 'premium_monthly',
        label: 'Monthly Premium',
        type: 'currency',
    },
    {
        id: 'premium_quarterly',
        label: 'Quarterly Premium',
        type: 'currency',
    },
    {
        id: 'premium_semi_annual',
        label: 'Semi-Annual Premium',
        type: 'currency',
    },
    {
        id: 'premium_annual',
        label: 'Annual Premium',
        type: 'currency',
    },
    {
        id: 'am_best_rating',
        label: 'A.M. Best Rating',
    },
]

const QuoteComparisonModal = ({
    isOpen,
    onClose,
    comparisonData,
    onShare
}) => {
    if (!isOpen) return null;

    const [tableHeader, setTableHeader] = useState([]);
    const [sortBy, setSortBy] = useState({ sort_by_column: {}, sort_direction: 'asc' });

    useEffect(() => {
        if (isOpen) {
            if (comparisonData?.activeTab === 'new-annuity') {
                setTableHeader(AAQ_QuoteCompare_TableColumn);
                setSortBy({ sort_by_column: AAQ_QuoteCompare_TableColumn.find(c => c.default) || {}, sort_direction: 'asc' });
            } else if (comparisonData?.activeTab === 'new-life') {
                let lifeColumns = [...ALQ_QuoteCompare_TableColumn];
                comparisonData?.productFeatures?.forEach(feature => {
                    lifeColumns.push({
                        id: feature.name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, ""), label: feature.name,
                        type: 'feature',
                        product_ids: feature.product_ids,
                    });
                });
                setTableHeader(lifeColumns);
                setSortBy({ sort_by_column: lifeColumns.find(c => c.default) || {}, sort_direction: 'asc' });
            }
        }
    }, [isOpen]);

    // Only close if clicking directly on the overlay, not on any child elements
    const handleOverlayClick = e => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Add click handler for modal content to prevent accidental closing
    const handleModalClick = e => {
        e.stopPropagation();
    };

    const onHandleSort = (column) => {
        setSortBy({
            sort_by_column: column,
            sort_direction: sortBy?.sort_direction === 'asc' ? 'desc' : 'asc'
        });
    }

    const renderMoreInfo = (item, ids) => {
        let tobaccoStatus = '';
        if (comparisonData?.activeTab === 'new-life' && ids[1] === 'tobacco') tobaccoStatus = item[ids[1]] ? 'Tobacco' : 'Non-Tobacco' || '';

        return (
            <div>
                <div style={styles.infoHeader}>{item[ids[0]] || ''}</div>
                <div style={styles.infoSubHeader}>{tobaccoStatus || item[ids[1]] || ''}</div>
            </div>
        )
    }

    const renderFeatureCheck = (item, column) => {
        if (column?.product_ids?.includes(item.product_id)) {
            return <i className='mdi mdi-check-bold' style={styles.checkMark} />;
        } else return null;
    }

    const formatters = {
        currency: (value) => value ? Numeral(value).format('$0,0.00') : 'N/A',
        percentage: (value) => value ? `${Number(value).toFixed(2)}%` : 'N/A',
        feature: (_, item, column) => renderFeatureCheck(item, column) || 'N/A'
    };

    const getFormattedValue = (item, column) => {
        const value = item?.[column?.id];
        const formatter = formatters[column?.type];

        if (formatter) {
            return formatter(value, item, column);
        }

        return value || 'N/A';
    };

    let sorted_data = comparisonData?.quotes || [];
    if (sortBy?.sort_by_column?.type === 'feature') {
        // Sort by whether the product has the feature (checkmark)
        sorted_data = [...sorted_data].sort((a, b) => {
            const aHasFeature = sortBy?.sort_by_column?.product_ids?.includes(a.product_id) ? 1 : 0;
            const bHasFeature = sortBy?.sort_by_column?.product_ids?.includes(b.product_id) ? 1 : 0;
            if (sortBy?.sort_direction === 'asc') {
                return aHasFeature - bHasFeature;
            } else {
                return bHasFeature - aHasFeature;
            }
        });
    } else {
        sorted_data = _orderBy(
            sorted_data,
            Array.isArray(sortBy?.sort_by_column) ? sortBy?.sort_by_column?.id[0] : sortBy?.sort_by_column?.id,
            sortBy?.sort_direction
        );
    }

    return (
        <div onClick={handleOverlayClick} style={styles.overlay}>
            <div onClick={handleModalClick} style={styles.modal}>
                {/* Header */}
                <div style={styles.header}>
                    <h2 style={styles.title}>Quote Comparison</h2>
                    <button onClick={onClose} style={styles.closeButton}><IoClose /></button>
                </div>
                {/* Body */}
                <div style={styles.container}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                {tableHeader?.map(column => (
                                    <th key={column?.id} style={{ ...styles.tableRow, ...styles.tableHeader }} onClick={() => onHandleSort(column)}>
                                        {column?.label || ''}
                                        {sortBy?.sort_by_column?.label === column?.label && (
                                            <i className={`mdi ${sortBy?.sort_direction === 'asc' ? 'mdi-chevron-up' : 'mdi-chevron-down'}`} style={styles.sortIcon} />
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted_data?.map((item, index) => (
                                <tr key={index}>
                                    {tableHeader?.map(column => (
                                        <td key={column?.id} style={{ ...styles.tableRow, ...styles.tableCell }}>
                                            {Array.isArray(column?.id) ?
                                                renderMoreInfo(item, column?.id) :
                                                <p style={styles.info}>
                                                    {getFormattedValue(item, column)}
                                                </p>
                                            }
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* Footer */}
                <div style={styles.footer}>
                    <button onClick={() => onShare()} style={styles.applyButton}>Share Quotes</button>
                </div>
            </div>
        </div >
    );
};

QuoteComparisonModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    comparisonData: PropTypes.object.isRequired,
    onShare: PropTypes.func.isRequired,
};

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    },
    modal: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '25px',
        maxWidth: '900px',
        width: '95%',
        maxHeight: '80vh',
        overflowY: 'auto',
        position: 'relative',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
        display: 'flex',
        flexDirection: 'column'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: '5px',
        borderBottom: '1px solid #e7e6e6',
    },
    title: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#333',
    },
    closeButton: {
        backgroundColor: '#f8f9fa',
        border: 'none',
        fontSize: '25px',
        cursor: 'pointer',
        color: '#333',
        padding: '5px',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        transition: 'all 0.2s ease'
    },
    container: {
        width: '100%',
        overflow: 'auto'
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        tableLayout: "fixed",
        backgroundColor: "#fff",
        margin: "20px 0px",
    },
    tableRow: {
        width: "200px",
        padding: "10px",
        textAlign: "center",
    },
    tableHeader: {
        color: "#FFF",
        backgroundColor: "#017DB7",
        border: "1px solid #000",
        cursor: 'pointer',
        textTransform: 'uppercase',
        fontWeight: FontWeights.MEDIUM,
        fontSize: FontSizes.SMALL,
        lineHeight: '1.2em',
    },
    sortIcon: {
        color: "#FFF",
        marginLeft: 5,
        fontSize: 14
    },
    arrowStyle: {
        color: "#FFF",
        marginLeft: 5,
        fontSize: 14
    },
    checkMark: {
        fontSize: 22,
        color: 'green'
    },
    tableCell: {
        border: "1px solid #000",
        fontSize: FontSizes.REGULAR,
        lineHeight: '1.4em',
    },
    info: {
        fontSize: '13px',
        fontWeight: '400',
        color: '#000',
    },
    infoHeader: {
        fontSize: FontSizes.MEDIUM,
        fontWeight: FontWeights.MEDIUM,
        color: '#000',
    },
    infoSubHeader: {
        fontSize: FontSizes.SMALL,
        fontStyle: 'italic',
        color: '#999',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingTop: '15px',
        marginTop: '15px',
        borderTop: '1px solid #e7e6e6',
    },
    applyButton: {
        padding: '10px 24px',
        backgroundColor: '#007bff',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
    },
};

export default QuoteComparisonModal;
